/**
 * PublicTeamPage — standalone full-page team roster (Iter32). Reached from
 * the public /auction/{id} view (Teams tab) or the Team-Owner overlay
 * (Teams tab) by clicking any team name. Matches the reference screenshot:
 * team name + count + prev/next arrows + category chips + per-category
 * counts + player cards with a download icon per player.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CaretLeft, CaretRight, DownloadSimple, ArrowLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { resolveImageUrl } from "@/components/ImageInput";

function cx(...arr) { return arr.filter(Boolean).join(" "); }

export default function PublicTeamPage() {
  const { auctionId, teamId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [category, setCategory] = useState("ALL");

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get(`/public/auctions/${auctionId}`);
        if (alive) setData(r.data);
      } catch { if (alive) setData({}); }
    };
    load();
    const t = setInterval(load, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [auctionId]);

  const enriched = useMemo(() => {
    if (!data?.teams) return [];
    return data.teams.map((t) => {
      const tp = (data.players || []).filter((p) => p.sold_to === t.id);
      return { ...t, players: tp };
    });
  }, [data]);

  const teamIdx = enriched.findIndex((t) => t.id === teamId);
  const t = enriched[teamIdx] || null;
  const categories = Array.from(new Set((t?.players || []).map((p) => p.category).filter(Boolean)));

  if (!data) return <FullPage>Loading team…</FullPage>;
  if (!t) return <FullPage>Team not found. <Link to={`/auction/${auctionId}`} className="text-yellow-300 underline ml-2">Back</Link></FullPage>;

  const list = category === "ALL" ? t.players
    : category === "No category" ? t.players.filter((p) => !p.category)
    : t.players.filter((p) => p.category === category);
  // icon first, then by number
  list.sort((a, b) => {
    const ai = (a.is_icon || a.category === "Icon") ? 0 : 1;
    const bi = (b.is_icon || b.category === "Icon") ? 0 : 1;
    if (ai !== bi) return ai - bi;
    return (a.player_number || 0) - (b.player_number || 0);
  });
  const catCounts = categories.reduce((acc, c) => {
    acc[c] = t.players.filter((p) => p.category === c).length;
    return acc;
  }, {});

  const go = (dir) => {
    if (enriched.length < 2) return;
    const next = (teamIdx + dir + enriched.length) % enriched.length;
    navigate(`/auction/${auctionId}/team/${enriched[next].id}`);
  };

  const bannerUrl = data.auction?.banner_url ? resolveImageUrl(data.auction.banner_url) : null;

  return (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ background: "linear-gradient(180deg,#071731 0%,#0A2350 60%,#082042 100%)" }} data-testid="public-team-page">
      {bannerUrl && (
        <img src={bannerUrl} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none select-none" />
      )}
      <div className="relative max-w-7xl mx-auto px-3 sm:px-6 py-4">
        <Link to={`/auction/${auctionId}`} className="text-xs text-yellow-300/80 hover:text-yellow-200 inline-flex items-center gap-1 mb-3" data-testid="team-back">
          <ArrowLeft size={12} /> Back to auction
        </Link>

        {/* Team header row */}
        <div className="rounded-xl bg-[#0F2C5A]/80 backdrop-blur border border-white/5 px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <button onClick={() => go(-1)} className="p-2 rounded-full text-yellow-300 hover:bg-yellow-400/20" data-testid="team-prev" aria-label="Previous team">
            <CaretLeft weight="bold" />
          </button>
          <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
            <div className="text-white font-bold text-lg sm:text-2xl truncate" data-testid="team-name">{t.name}</div>
            <button onClick={() => go(1)} className="p-1 rounded-full text-yellow-300 hover:bg-yellow-400/20" data-testid="team-next" aria-label="Next team">
              <CaretRight weight="bold" />
            </button>
            <span className="text-yellow-300 font-black text-2xl sm:text-3xl tabular-nums" data-testid="team-count">{t.players.length}</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
            {["ALL", ...categories, categories.length ? "No category" : null].filter(Boolean).map((c) => (
              <button key={c} onClick={() => setCategory(c)}
                className={cx(
                  "text-[10px] sm:text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-md whitespace-nowrap transition-colors border",
                  category === c
                    ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 border-yellow-300"
                    : "bg-transparent text-white/80 border-white/10 hover:border-yellow-400/50 hover:text-yellow-300",
                )}
                data-testid={`team-cat-${c}`}
              >{c}</button>
            ))}
          </div>
        </div>

        {/* Per-category counts row */}
        <div className="mt-3 rounded-xl bg-[#0F2C5A]/80 backdrop-blur border border-white/5 px-4 py-2 flex items-center gap-6 flex-wrap justify-center text-white/85 text-sm">
          {Object.entries(catCounts).length === 0 && <span className="text-white/40 text-xs">No categorised players yet.</span>}
          {Object.entries(catCounts).map(([c, n]) => {
            const total = (data.players || []).filter((p) => p.category === c).length;
            return <span key={c} className="tabular-nums" data-testid={`team-cat-count-${c}`}>{c}: <span className="text-white font-bold">{n}/{total}</span></span>;
          })}
        </div>

        {/* Player card grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 pt-3 pb-8">
          {list.length === 0 && <div className="col-span-full text-center text-white/60 py-10">No players in this filter.</div>}
          {list.map((p) => (
            <PlayerCard key={p.id} p={p} auctionId={auctionId} teamId={t.id} isCaptain={p.id === t.captain_id} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PlayerCard({ p, auctionId, teamId, isCaptain }) {
  const isIcon = p.is_icon || p.category === "Icon";
  const pts = p.sold_price != null ? p.sold_price : p.base_price;
  return (
    <div className={cx("rounded-xl border relative overflow-hidden", isIcon ? "bg-[#1a2a4a] border-amber-400/70 ring-1 ring-amber-400/30" : "bg-[#0F2C5A] border-white/5")} data-testid={`team-player-${p.id}`}>
      {p.player_number != null && (
        <span className="absolute top-2 left-2 z-10 bg-gradient-to-br from-amber-400 to-yellow-500 text-slate-900 font-black text-xs px-2 py-0.5 rounded-md shadow">{p.player_number}</span>
      )}
      {isCaptain && (
        <span className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-rose-500 text-white font-black text-[9px] px-2 py-0.5 rounded-md tracking-widest shadow">CAPTAIN</span>
      )}
      <a
        href={`/auction/${auctionId}/player/${p.id}/card`}
        target="_blank"
        rel="noreferrer"
        className="absolute top-2 right-2 z-10 rounded-md bg-black/70 hover:bg-yellow-400 hover:text-slate-900 text-yellow-300 p-1.5 transition-colors"
        title="Open downloadable player card"
        data-testid={`team-player-download-${p.id}`}
        aria-label="Open downloadable player card"
      >
        <DownloadSimple weight="bold" size={16} />
      </a>
      <div className="flex items-stretch gap-3 p-3">
        <div className="relative w-24 h-28 sm:w-28 sm:h-32 rounded-md overflow-hidden bg-black/30 flex-shrink-0">
          {p.photo_url ? (
            <img src={resolveImageUrl(p.photo_url)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <div className="w-full h-full grid place-items-center text-white/40 text-[10px]">NO PHOTO</div>
          )}
          {isIcon ? (
            <span className="absolute bottom-0 inset-x-0 text-center text-[10px] font-black uppercase tracking-widest text-slate-900 bg-gradient-to-r from-amber-400 to-yellow-500 py-0.5 truncate px-1">{p.name}</span>
          ) : p.category && (
            <span className="absolute bottom-0 inset-x-0 text-center text-[10px] font-black uppercase tracking-widest text-emerald-300 bg-black/50 py-0.5">{p.category}</span>
          )}
        </div>
        <div className="flex-1 min-w-0 pl-1">
          <div className="text-yellow-300 font-black text-lg leading-tight truncate">{p.name}</div>
          {pts != null && (
            <div className="text-white text-sm mt-1">
              <span className="text-white/70">Points : </span>
              <span className="font-black tabular-nums text-white">{pts.toLocaleString()}</span>
            </div>
          )}
          {p.role && <div className="text-white/85 text-sm truncate">{p.role}</div>}
          {p.batting_style && <div className="text-white/70 text-sm truncate">{p.batting_style}</div>}
          {p.bowling_style && <div className="text-white/70 text-sm truncate">{p.bowling_style}</div>}
        </div>
      </div>
    </div>
  );
}

function FullPage({ children }) {
  return <div className="min-h-screen grid place-items-center label-mono text-yellow-300/80" style={{ background: "#071731" }}>{children}</div>;
}
